/**
 *
 * @class StateModel
 * @desc A model representing the state of the player at any given moment
 * @module StateModel
 *
 */
class StateModel {

    /**
     *
     * @constructor
     *
     */
    constructor() {

        /**
         *
         * @type {boolean}
         * @desc If the player is playing or not
         *
         */
        this.playing = false;

        /**
         *
         * @type {boolean}
         * @desc If the player is paused or not
         *
         */
        this.paused = true;

        /**
         *
         * @type {boolean}
         * @desc If the player has stopped. Stopped means has played through and returned to the beginning (replay) or `MEDIA_COMPLETE` event.
         *
         */
        this.stopped = true;


        /**
         *
         * @type {boolean}
         * @desc If the player is in a "loading" state. Loading state is 1) not playing and 2) loading data to be ready to play
         *
         */
        this.loading = false;

        /**
         *
         * @type {boolean}
         * @desc The media has started playback. Set `true` on the `MEDIA_START` event
         *
         */
        this.mediaStarted = false;

        /**
         *
         * @type {object}
         * @desc Representation of the volume. The object contains 3 properties
         * 1. value: {number} The value of the current volume setting 0 - 1.
         * 2. muted: {boolean} If the player is muted or not.
         * 3. lastValue: {number} The value of the last volume setting 0 - 1.
         *
         */
        this.volume = {

            value: 1,
            muted: false,
            lastValue: 1,

        };

        /**
         *
         * @type {number}
         * @desc The buffered amount in seconds.
         *
         */
        this.bufferedValue = 0;

        /**
         *
         * @type {boolean}
         * @desc If an ad is playing or not (not used currently).
         *
         */
        this.isAdPlaying = false;

        /**
         *
         * @type {boolean}
         * @desc If fullscreen is enabled by browser or user settings or not.
         *
         */
        this.isFullscreenEnabled = true;

        /**
         *
         * @type {boolean}
         * @desc If the player is in fullscreen mode or not.
         *
         */
        this.isFullscreen = false;

        /**
         *
         * @type {ArrayLike object}
         * @desc Keeps closed captions tracks in the state
         *
         */
        this.closedCaptions = {};

        /**
         *
         * @type {boolean}
         * @desc Determines whether closed captions are turned on or off
         *
         */
        this.closedCaptionsActive = false;

        /**
         *
         * @type {boolean}
         * @desc If the media is audio only or not (not used currently).
         *
         */
        this.isAudioOnly = false;

        /**
         *
         * @type {*}
         * @desc The currently selected audio track (not currently used).
         *
         */
        this.selectedAudioTrack = undefined;

        /**
         *
         * @type {number}
         * @desc The currently selected bitrate (not currently used).
         *
         */
        this.selectedBitRate = -1;

    }

    /**
     *
     * @param {boolean} isLoading
     * @desc If the player is in a loading state or not
     *
     */
    setLoadingState(isLoading) {

        this.loading = isLoading;

    }

    /**
     *
     * @param {number} volume The current volume between 0 and 1
     * @param {boolean} muted If the player is muted or not
     * @desc Sets the player state when the `MEDIA_START` event is received. In addition to setting
     * `playing` to `true`, `paused` to `false`, & `stopped` to `false`, `mediaStarted` is set to `true`.
     *
     */
    setMediaStartState(volume, muted) {

        this.playing = true;
        this.paused = false;
        this.stopped = false;
        this.mediaStarted = true;

        // Also set the initial volume state
        this.volume.value = volume || this.volume.value;
        this.volume.muted = muted || this.volume.muted;

    }

    /**
     *
     * @desc Sets the player sate when the MEDIA_PAUSE event is dispatched
     *
     */
    setMediaPauseState() {

        this.playing = false;
        this.paused = true;
        this.stopped = false;

    }

    /**
     *
     * @desc Sets the player sate when the MEDIA_COMPLETE event is dispatched
     *
     */
    setMediaCompleteState() {

        this.playing = false;
        this.paused = true;
        this.stopped = true;

    }

    /**
     *
     * @desc Set the volume. When setting the volume, capture muted and previous volume.
     * @param {number} volume The volume
     * @param {boolean} muted If the player is muted or not
     *
     */
    setVolumeChangeState(volume, muted) {

        this.volume.lastValue = this.volume.value;
        this.volume.value = volume;
        this.volume.muted = muted;

    }

    /**
     *
     * @desc Set isFullscreen state data
     *
     */
    setFullscreenChangeState(isFullscreen, isFullscreenEnabled) {

        this.isFullscreen = isFullscreen;
        this.isFullscreenEnabled = isFullscreenEnabled;

    }

    /**
     *
     * @desc Update adIsPlaying
     * @param {boolean} adIsPlaying If an ad is playing or not
     *
     */
    setIsAdPlayingState(adIsPlaying) {

        this.isAdPlaying = adIsPlaying;

    }

    /**
     *
     * @desc Update closedCaptions
     * @param {array-like} vttItems List of vttItems or text tracks
     *
     */
    setClosedCaptions(vttItems) {

        this.closedCaptions = (vttItems || {});

    }

    /**
     *
     * @desc Update closedCaptionsActive
     * @param {boolean} ccActive If closed captions are showing
     *
     */
    setClosedCaptionsActiveState(ccActive) {

        this.closedCaptionsActive = ccActive;

    }

}

export default StateModel;
